import React from "react";
import {
  formatCurrency,
  formatNumber,
  formatPosition,
} from "../../../utils/dataCalculations";
import {
  createColumn,
  formatAggregatedFooter,
  formatNumericCell,
  formatPriceCell,
  getCommonBodyCellProps,
  getCommonFooterCellProps,
  getCommonHeadCellProps,
} from "./commonUtils";

export const getPnLTableColumns = (theme) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      140,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      120,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "total_pnl",
      "Total P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "total_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Current Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_trading_quantity",
      "Net Trade Today",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "last_closing_price",
      "Previous Close",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "market_value",
      "Market Value",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "market_value",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "lmv",
      "LMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA > 0 ? marketValueA : 0;
            const valueB = marketValueB > 0 ? marketValueB : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue > 0 ? marketValue : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue > 0 ? marketValue : 0;
            return formatNumericCell(
              value,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue > 0 ? marketValue : 0);
              }, 0);
            return formatNumericCell(
              total,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "smv",
      "SMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA < 0 ? Math.abs(marketValueA) : 0;
            const valueB = marketValueB < 0 ? Math.abs(marketValueB) : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue < 0 ? Math.abs(marketValue) : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue < 0 ? Math.abs(marketValue) : 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue < 0 ? Math.abs(marketValue) : 0);
              }, 0);
            if (total === 0) return "-";
            const formattedTotal = `(${formatCurrency(total).replace(
              "$",
              ""
            )})`;
            return (
              <span
                style={{
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sod_quantity",
      "SOD Quantity",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_quantity",
      "Buy Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_price",
      "Buy Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_quantity",
      "Sell Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_price",
      "Sell Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_quantity",
      "Short Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_price",
      "Short Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_quantity",
      "Cover Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_price",
      "Cover Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_fx_rate",
      "FX Rate",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_source",
      "Pricing Source",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_updated_timestamp",
      "Last Update Time",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
  ];
};

export const getPnlTableColumnsEthan1 = (theme) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      140,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "total_pnl",
      "Total P&L",
      140,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "total_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
  ];
};

export const getSpacPnlTableColumns = (theme) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      140,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      120,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "total_pnl",
      "Total P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "total_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Current Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_trading_quantity",
      "Net Trade Today",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "last_closing_price",
      "Previous Close",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "volume",
      "Volume",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "market_value",
      "Market Value",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "market_value",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "lmv",
      "LMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA > 0 ? marketValueA : 0;
            const valueB = marketValueB > 0 ? marketValueB : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue > 0 ? marketValue : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue > 0 ? marketValue : 0;
            return formatNumericCell(
              value,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue > 0 ? marketValue : 0);
              }, 0);
            return formatNumericCell(
              total,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "smv",
      "SMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA < 0 ? Math.abs(marketValueA) : 0;
            const valueB = marketValueB < 0 ? Math.abs(marketValueB) : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue < 0 ? Math.abs(marketValue) : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue < 0 ? Math.abs(marketValue) : 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue() || 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue < 0 ? Math.abs(marketValue) : 0);
              }, 0);
            if (total === 0) return "-";
            const formattedTotal = `(${formatCurrency(total).replace(
              "$",
              ""
            )})`;
            return (
              <span
                style={{
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sod_quantity",
      "SOD Quantity",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_quantity",
      "Buy Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_price",
      "Buy Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_quantity",
      "Sell Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_price",
      "Sell Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_quantity",
      "Short Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_price",
      "Short Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_quantity",
      "Cover Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_price",
      "Cover Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_fx_rate",
      "FX Rate",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_source",
      "Pricing Source",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_updated_timestamp",
      "Last Update Time",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
  ];
};

export const getConvertsPnlTableColumns = (theme) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      140,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      160,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "total_pnl",
      "Total P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "total_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Current Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_trading_quantity",
      "Net Trade Today",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "last_closing_price",
      "Previous Close",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "market_value",
      "Market Value",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "market_value",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "lmv",
      "LMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA > 0 ? marketValueA : 0;
            const valueB = marketValueB > 0 ? marketValueB : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue > 0 ? marketValue : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue > 0 ? marketValue : 0;
            return formatNumericCell(
              value,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue > 0 ? marketValue : 0);
              }, 0);
            return formatNumericCell(
              total,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "smv",
      "SMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA < 0 ? Math.abs(marketValueA) : 0;
            const valueB = marketValueB < 0 ? Math.abs(marketValueB) : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue < 0 ? Math.abs(marketValue) : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue < 0 ? Math.abs(marketValue) : 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue() || 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue < 0 ? Math.abs(marketValue) : 0);
              }, 0);
            if (total === 0) return "-";
            const formattedTotal = `(${formatCurrency(total).replace(
              "$",
              ""
            )})`;
            return (
              <span
                style={{
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sod_quantity",
      "SOD Quantity",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_quantity",
      "Buy Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_price",
      "Buy Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_quantity",
      "Sell Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_price",
      "Sell Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_quantity",
      "Short Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_price",
      "Short Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_quantity",
      "Cover Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_price",
      "Cover Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_fx_rate",
      "FX Rate",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_source",
      "Pricing Source",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_updated_timestamp",
      "Last Update Time",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
  ];
};

export const getOtherStrategiesPnlTableColumns = (theme) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      140,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      120,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "total_pnl",
      "Total P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "total_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Current Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_trading_quantity",
      "Net Trade Today",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "last_closing_price",
      "Previous Close",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "market_value",
      "Market Value",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "market_value",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "lmv",
      "LMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA > 0 ? marketValueA : 0;
            const valueB = marketValueB > 0 ? marketValueB : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue > 0 ? marketValue : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue > 0 ? marketValue : 0;
            return formatNumericCell(
              value,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue > 0 ? marketValue : 0);
              }, 0);
            return formatNumericCell(
              total,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "smv",
      "SMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA < 0 ? Math.abs(marketValueA) : 0;
            const valueB = marketValueB < 0 ? Math.abs(marketValueB) : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue < 0 ? Math.abs(marketValue) : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue < 0 ? Math.abs(marketValue) : 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue() || 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue < 0 ? Math.abs(marketValue) : 0);
              }, 0);
            if (total === 0) return "-";
            const formattedTotal = `(${formatCurrency(total).replace(
              "$",
              ""
            )})`;
            return (
              <span
                style={{
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sod_quantity",
      "SOD Quantity",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_quantity",
      "Buy Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_price",
      "Buy Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_quantity",
      "Sell Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_price",
      "Sell Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_quantity",
      "Short Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_price",
      "Short Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_quantity",
      "Cover Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatNumber,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_price",
      "Cover Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_fx_rate",
      "FX Rate",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_source",
      "Pricing Source",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_updated_timestamp",
      "Last Update Time",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
  ];
};

export const getEventDrivenPnlTableColumns = (theme) => {
  const commonHeadCellProps = getCommonHeadCellProps(theme);
  const commonBodyCellProps = getCommonBodyCellProps(theme);
  const commonFooterCellProps = getCommonFooterCellProps(theme);

  return [
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "StrategyName",
      "Strategy Name",
      140,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "security_name",
      "Security Name",
      120,
      "left",
      {
        body: {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "total_pnl",
      "Total P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "total_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "trading_pnl",
      "Trading P&L",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.success.main
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "trading_pnl",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "realtime_quantity",
      "Current Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "net_trading_quantity",
      "Net Trade Today",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_price",
      "Current Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "last_closing_price",
      "Previous Close",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "market_value",
      "Market Value",
      120,
      "right",
      {
        other: {
          aggregationFn: "sum",
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) =>
            formatAggregatedFooter(
              table,
              "market_value",
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "lmv",
      "LMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA > 0 ? marketValueA : 0;
            const valueB = marketValueB > 0 ? marketValueB : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue > 0 ? marketValue : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue > 0 ? marketValue : 0;
            return formatNumericCell(
              value,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
          AggregatedCell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue > 0 ? marketValue : 0);
              }, 0);
            return formatNumericCell(
              total,
              formatCurrency,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "smv",
      "SMV",
      120,
      "right",
      {
        other: {
          sortingFn: (rowA, rowB) => {
            const marketValueA = rowA.original.market_value || 0;
            const marketValueB = rowB.original.market_value || 0;
            const valueA = marketValueA < 0 ? Math.abs(marketValueA) : 0;
            const valueB = marketValueB < 0 ? Math.abs(marketValueB) : 0;
            return valueA - valueB;
          },
          aggregationFn: (columnId, leafRows) => {
            let total = 0;
            for (const row of leafRows) {
              const marketValue = row.original.market_value || 0;
              total += marketValue < 0 ? Math.abs(marketValue) : 0;
            }
            return total;
          },
          Cell: ({ row }) => {
            const marketValue = row.original.market_value || 0;
            const value = marketValue < 0 ? Math.abs(marketValue) : 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          AggregatedCell: ({ cell }) => {
            const value = cell.getValue() || 0;
            if (value === 0) return "-";
            const formattedValue = `(${formatCurrency(value).replace(
              "$",
              ""
            )})`;
            return (
              <span style={{ color: theme.palette.error.main }}>
                {formattedValue}
              </span>
            );
          },
          Footer: ({ table }) => {
            const total = table
              .getFilteredRowModel()
              .rows.reduce((sum, row) => {
                const marketValue = row.original.market_value || 0;
                return sum + (marketValue < 0 ? Math.abs(marketValue) : 0);
              }, 0);
            if (total === 0) return "-";
            const formattedTotal = `(${formatCurrency(total).replace(
              "$",
              ""
            )})`;
            return (
              <span
                style={{
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              >
                {formattedTotal}
              </span>
            );
          },
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sod_quantity",
      "SOD Quantity",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_quantity",
      "Buy Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "buy_trade_price",
      "Buy Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_quantity",
      "Sell Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "sell_trade_price",
      "Sell Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_quantity",
      "Short Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "short_trade_price",
      "Short Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_quantity",
      "Cover Qty",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) =>
            formatNumericCell(
              cell.getValue(),
              formatPosition,
              theme.palette.error.main,
              theme.palette.primary.contrastText
            ),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "cover_trade_price",
      "Cover Price",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 3),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "current_fx_rate",
      "FX Rate",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => formatPriceCell(cell.getValue(), 4),
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_source",
      "Pricing Source",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
    createColumn(
      commonHeadCellProps,
      commonBodyCellProps,
      commonFooterCellProps,
      "pricing_updated_timestamp",
      "Last Update Time",
      120,
      "right",
      {
        other: {
          Cell: ({ cell }) => cell.getValue() || "-",
        },
      }
    ),
  ];
};
